import {CheckCircle, XCircle, Clock, Ban, MessageSquare} from 'lucide-react'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {formatTimestamp} from '@/lib/date-utils'
import {permissions} from '@/lib/permissions'
import {ApprovalDecisionBadge, ApprovalStepStatus} from '@/types/access-request'
import type {ApprovalStep, AccessRequestApprovalResponse} from '@/types/access-request'
import {ApprovalStrategy, ResolverType} from '@/types/approval-policy'

interface ApprovalChainProps {
    steps: ApprovalStep[];
    onApprove?: (itemId: string) => void;
    onReject?: (itemId: string) => void;
    itemId: string;
    requiredPermission?: string;
}

const STEP_ICON_PENDING = '\u25CB'
const STEP_ICON_ACTIVE = '\u25CF'

function renderDecision(decision: AccessRequestApprovalResponse, i18n: ReturnType<typeof useI18n>) {
    const decisionBadge = ApprovalDecisionBadge.of(decision.decision)
    return (
        <>
            <span className={`${decisionBadge?.textClass ?? 'text-muted-foreground'} ml-2`}>
                {decisionBadge ? decisionBadge.label(i18n) : decision.decision}
            </span>
            <span className="text-muted-foreground text-xs ml-2">
                {formatTimestamp(decision.decidedAt)}
            </span>
            {decision.comment && (
                <p className="text-muted-foreground text-xs mt-0.5 flex items-start gap-1">
                    <MessageSquare className="h-3 w-3 mt-0.5 shrink-0"/>
                    {decision.comment}
                </p>
            )}
        </>
    )
}

export function ApprovalChain({steps, onApprove, onReject, itemId, requiredPermission}: ApprovalChainProps) {
    const {user, hasPermission} = useAuth()
    const i18n = useI18n()

    const resolverTypeLabels: Record<string, string> = {
        [ResolverType.RESOURCE_OWNER]: i18n.approvalChain.resolverResourceOwner,
        [ResolverType.PROFILE_OWNER]: i18n.approvalChain.resolverProfileOwner,
        [ResolverType.MANAGER]: i18n.approvalChain.resolverManager,
        [ResolverType.SPECIFIC_IDENTITY]: i18n.approvalChain.resolverSpecificIdentity,
        [ResolverType.TEAM_MEMBER]: i18n.approvalChain.resolverTeamMember,
    }

    const stepStatusIndicator = (status: ApprovalStepStatus) => {
        switch (status) {
            case ApprovalStepStatus.PENDING:
                return <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-muted-foreground/40 text-muted-foreground/40 text-xs">{STEP_ICON_PENDING}</span>
            case ApprovalStepStatus.ACTIVE:
                return <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-blue-500 bg-status-info/10 text-status-info text-xs animate-pulse">{STEP_ICON_ACTIVE}</span>
            case ApprovalStepStatus.APPROVED:
                return <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-green-500 bg-status-success/10 text-status-success"><CheckCircle className="h-3.5 w-3.5" /></span>
            case ApprovalStepStatus.REJECTED:
                return <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-red-500 bg-status-danger/10 text-status-danger"><XCircle className="h-3.5 w-3.5" /></span>
            case ApprovalStepStatus.SKIPPED:
                return <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-muted-foreground/30 bg-muted/50 text-muted-foreground/50"><Ban className="h-3.5 w-3.5" /></span>
        }
    }

    const stepStatusBadge = (status: ApprovalStepStatus) => {
        switch (status) {
            case ApprovalStepStatus.PENDING:
                return <Badge variant="outline" className="text-xs">{i18n.approvalChain.statusPending}</Badge>
            case ApprovalStepStatus.ACTIVE:
                return <Badge variant="secondary" className="text-xs gap-1"><Clock className="h-3 w-3" />{i18n.approvalChain.statusActive}</Badge>
            case ApprovalStepStatus.APPROVED:
                return <Badge variant="success" className="text-xs gap-1"><CheckCircle className="h-3 w-3" />{i18n.approvalChain.statusApproved}</Badge>
            case ApprovalStepStatus.REJECTED:
                return <Badge variant="destructive" className="text-xs gap-1"><XCircle className="h-3 w-3" />{i18n.approvalChain.statusRejected}</Badge>
            case ApprovalStepStatus.SKIPPED:
                return <Badge variant="outline" className="text-xs text-muted-foreground">{i18n.approvalChain.statusSkipped}</Badge>
        }
    }

    const isCurrentUserApproverInStep = (step: ApprovalStep) =>
        step.status === ApprovalStepStatus.ACTIVE &&
        step.approvers.some(a => a.approver.id === user?.identityId)

    return (
        <div className="space-y-0">
            {steps.map((step, index) => (
                <div key={step.id} className="relative">
                    {/* Connector line between steps */}
                    {index < steps.length - 1 && (
                        <div className="absolute left-3 top-8 bottom-0 w-px bg-border" />
                    )}

                    <div className="flex gap-3 pb-6">
                        {/* Step indicator */}
                        <div className="flex-shrink-0 pt-0.5">
                            {stepStatusIndicator(step.status)}
                        </div>

                        {/* Step content */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium">
                                    {i18n.approvalChain.stepLabel({step: step.stepOrder.toString()})}
                                    {': '}
                                    {resolverTypeLabels[step.resolverType] ?? step.resolverType}
                                    {step.resolverParam && ` (${step.resolverParam})`}
                                </span>
                                {stepStatusBadge(step.status)}
                            </div>

                            <div className="text-xs text-muted-foreground mt-1">
                                {i18n.approvalChain.strategy}: {step.approvalStrategy === ApprovalStrategy.ANY
                                    ? i18n.approvalChain.strategyAny
                                    : i18n.approvalChain.strategyAll}
                                {step.approvers.length > 0 && ` (${step.approvers.length})`}
                            </div>

                            {/* Approvers list */}
                            {step.approvers.length > 0 && (
                                <div className="mt-2 space-y-1">
                                    {step.approvers.map((approver, index) => {
                                        const decision = step.decisions.find(
                                            d => d.approver.id === approver.approver.id
                                        )
                                        const isLast = index === step.approvers.length - 1
                                        return (
                                            <div key={approver.id} className="flex items-start gap-2 text-sm">
                                                <span
                                                    className="text-muted-foreground"
                                                    data-testid={isLast ? 'approver-connector-last' : 'approver-connector-intermediate'}
                                                >{isLast ? '\u2514\u2500' : '\u251C\u2500'}</span>
                                                <div className="flex-1">
                                                    <span className="font-medium text-sm">{approver.approver.displayName}</span>
                                                    {decision ? renderDecision(decision, i18n) : step.status === ApprovalStepStatus.ACTIVE ? (
                                                        <span className="text-muted-foreground ml-2 text-xs">({i18n.approvalChain.pending})</span>
                                                    ) : step.status === ApprovalStepStatus.PENDING ? (
                                                        <span className="text-muted-foreground ml-2 text-xs italic">({i18n.approvalChain.preliminary})</span>
                                                    ) : null}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}

                            {/* Action buttons for active step */}
                            {isCurrentUserApproverInStep(step) && hasPermission(requiredPermission ?? permissions.accessRequests.approve) && (
                                <div className="flex gap-2 mt-3">
                                    <Button size="sm" onClick={() => onApprove?.(itemId)} data-testid="btn-approve">
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        {i18n.common.approve}
                                    </Button>
                                    <Button size="sm" variant="destructive" onClick={() => onReject?.(itemId)} data-testid="btn-reject">
                                        <XCircle className="h-3 w-3 mr-1" />
                                        {i18n.common.reject}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}
